import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Eye, Search, ShieldCheck, X } from 'lucide-react';
import type { Bootstrap, WorkspaceLeader } from '../shared/contracts';
import { api, setWorkspaceView } from './api';
import './WorkspaceSwitch.css';

export async function exitWorkspaceView() {
  await api('/workspace/exit', {});
  setWorkspaceView(null);
  location.reload();
}
export function WorkspaceSwitch({ data, openAccess }: { data: Bootstrap; openAccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [leaders, setLeaders] = useState<WorkspaceLeader[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const view = data.workspace?.view;
  const canSwitch = data.workspace?.canSwitch ?? data.user.role === 'super_user';
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true); setError(''); searchInput.current?.focus();
    void api<{ leaders: WorkspaceLeader[] }>('/workspace/leaders').then(result => { if (active) setLeaders(result.leaders); })
      .catch(e => { if (active) setError(e.message); }).finally(() => { if (active) setLoading(false); });
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape);
    return () => { active = false; document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  async function select(id: string | null) {
    setBusy(true); setError('');
    try {
      if (!id) await exitWorkspaceView();
      else {
        const result = await api<{ token: string }>('/workspace/view', { leaderId: id });
        setWorkspaceView(result.token); location.reload();
      }
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }
  const shown = leaders.filter(l => `${l.name} ${l.teamNames.join(' ')}`.toLowerCase().includes(search.trim().toLowerCase()));
  return <div className="workspace-picker" ref={root}>
    <button ref={button} type="button" className="workspace-switch" aria-label="Switch workspace view" aria-expanded={open} aria-controls="workspace-views" disabled={!canSwitch || busy} onClick={() => setOpen(!open)}>
      <span className="workspace-initial">{view ? <Eye size={17}/> : 'R'}</span>
      <span className="workspace-switch-copy"><strong>{view ? view.leaderName : 'Routing workspace'}</strong><small>{view ? 'Team leader view' : data.user.role === 'super_user' ? 'All teams · Super user' : 'My teams'}</small></span>
      {canSwitch && <ChevronDown size={14}/>}
    </button>
    {open && <section className="workspace-options" id="workspace-views" aria-label="Workspace views" aria-busy={busy || loading}>
      <div className="workspace-options-heading"><strong>View workspace as</strong><button type="button" className="icon-button" aria-label="Close workspace views" onClick={() => { setOpen(false); button.current?.focus(); }}><X size={16}/></button></div>
      <button className="workspace-option own-workspace" disabled={busy || !view} onClick={() => void select(null)}><ShieldCheck size={18}/><span><strong>Your workspace</strong><small>Super user · All teams</small></span>{!view && <Check size={15}/>}</button>
      <label className="workspace-search"><Search size={15}/><input ref={searchInput} aria-label="Search team leaders" placeholder="Search leaders or teams" value={search} onChange={e => setSearch(e.target.value)}/></label>
      <div className="workspace-leader-list">{loading ? <p className="workspace-picker-note">Loading team leaders…</p> : shown.map(l => <button className={`workspace-option ${view?.leaderId === l.id ? 'selected' : ''}`} key={l.id} disabled={busy} onClick={() => void select(l.id)}><Eye size={17}/><span><strong>{l.name}</strong><small>{l.teamNames.join(' · ') || 'No current team access'}</small></span>{view?.leaderId === l.id && <Check size={15}/>}</button>)}
        {!loading && !error && !shown.length && <p className="workspace-picker-note">{leaders.length ? 'No leaders match your search.' : 'No team leader accounts are enabled yet.'}</p>}
      </div>
      {error && <p className="workspace-picker-note form-error" role="alert">{error}</p>}
      <div className="workspace-picker-footer"><span>Read-only · This browser tab only</span>{!view && <button className="button text" onClick={() => { setOpen(false); openAccess(); }}>Manage team access</button>}</div>
    </section>}
  </div>;
}
